import type { DetectionBox } from './Detector';

export type TeamId = 0 | 1;

export interface ColorSample {
  r: number;
  g: number;
  b: number;
}

export interface TeamAssignment {
  teamId: TeamId;
  color: string;
}

export const TEAM_COLORS: readonly string[] = ['#3aa3ff', '#ff6b4a'];

const HISTORY = 10;

interface TrackColor {
  samples: ColorSample[];
  mean: ColorSample;
}

export class TeamClassifier {
  private readonly trackColors = new Map<number, TrackColor>();
  private assignments = new Map<number, TeamId>();
  private centroids: [ColorSample, ColorSample] | null = null;

  reset(): void {
    this.trackColors.clear();
    this.assignments.clear();
    this.centroids = null;
  }

  drop(ids: readonly number[]): void {
    for (const id of ids) {
      this.trackColors.delete(id);
      this.assignments.delete(id);
    }
  }

  observe(trackId: number, sample: ColorSample): void {
    let entry = this.trackColors.get(trackId);
    if (!entry) {
      entry = { samples: [], mean: { ...sample } };
      this.trackColors.set(trackId, entry);
    }
    entry.samples.push(sample);
    if (entry.samples.length > HISTORY) entry.samples.shift();
    entry.mean = averageColor(entry.samples);
  }

  classify(): void {
    const ids = Array.from(this.trackColors.keys());
    if (ids.length < 2) {
      this.assignments.clear();
      this.centroids = null;
      return;
    }
    const points = ids.map((id) => this.trackColors.get(id)!.mean);
    const { centroids, labels } = kmeans2(points);
    this.centroids = centroids;
    this.assignments.clear();
    for (let i = 0; i < ids.length; i += 1) {
      this.assignments.set(ids[i], labels[i]);
    }
  }

  assignmentFor(trackId: number): TeamAssignment | null {
    const t = this.assignments.get(trackId);
    if (t === undefined) return null;
    return { teamId: t, color: TEAM_COLORS[t] };
  }

  getAssignments(): ReadonlyMap<number, TeamId> {
    return this.assignments;
  }

  getCentroids(): readonly [ColorSample, ColorSample] | null {
    return this.centroids;
  }
}

export function averageColor(samples: readonly ColorSample[]): ColorSample {
  if (samples.length === 0) return { r: 0, g: 0, b: 0 };
  let r = 0;
  let g = 0;
  let b = 0;
  for (const s of samples) {
    r += s.r;
    g += s.g;
    b += s.b;
  }
  return { r: r / samples.length, g: g / samples.length, b: b / samples.length };
}

export function colorDistance(a: ColorSample, b: ColorSample): number {
  const dr = a.r - b.r;
  const dg = a.g - b.g;
  const db = a.b - b.b;
  return Math.sqrt(dr * dr + dg * dg + db * db);
}

export function kmeans2(points: readonly ColorSample[]): {
  centroids: [ColorSample, ColorSample];
  labels: TeamId[];
} {
  if (points.length < 2) {
    throw new Error('kmeans2 requires at least 2 points');
  }
  let c0 = points[0];
  let c1 = points[1];
  let best = colorDistance(c0, c1);
  for (let i = 0; i < points.length; i += 1) {
    for (let j = i + 1; j < points.length; j += 1) {
      const d = colorDistance(points[i], points[j]);
      if (d > best) {
        best = d;
        c0 = points[i];
        c1 = points[j];
      }
    }
  }
  let labels: TeamId[] = Array.from({ length: points.length }, () => 0 as TeamId);
  for (let iter = 0; iter < 10; iter += 1) {
    labels = points.map((p) =>
      colorDistance(p, c0) <= colorDistance(p, c1) ? (0 as TeamId) : (1 as TeamId),
    );
    const g0 = points.filter((_, i) => labels[i] === 0);
    const g1 = points.filter((_, i) => labels[i] === 1);
    if (g0.length === 0 || g1.length === 0) break;
    const nc0 = averageColor(g0);
    const nc1 = averageColor(g1);
    if (colorDistance(c0, nc0) < 0.5 && colorDistance(c1, nc1) < 0.5) {
      c0 = nc0;
      c1 = nc1;
      break;
    }
    c0 = nc0;
    c1 = nc1;
  }
  return { centroids: [c0, c1], labels };
}

export interface ColorSamplerDeps {
  offscreen: OffscreenCanvas | HTMLCanvasElement;
}

export function sampleUpperBodyColor(
  video: HTMLVideoElement,
  box: DetectionBox,
  deps: ColorSamplerDeps,
): ColorSample | null {
  const w = video.videoWidth;
  const h = video.videoHeight;
  if (w <= 0 || h <= 0) return null;
  const sx = Math.max(0, box.x * w);
  const sy = Math.max(0, box.y * h);
  const sw = Math.max(1, Math.min(w - sx, box.w * w));
  const shFull = Math.max(1, Math.min(h - sy, box.h * h));
  const sh = Math.max(1, shFull * 0.4);
  const target = 12;
  const canvas = deps.offscreen;
  canvas.width = target;
  canvas.height = target;
  const ctx = canvas.getContext('2d') as
    | OffscreenCanvasRenderingContext2D
    | CanvasRenderingContext2D
    | null;
  if (!ctx) return null;
  try {
    ctx.clearRect(0, 0, target, target);
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, target, target);
    const data = ctx.getImageData(0, 0, target, target).data;
    let r = 0;
    let g = 0;
    let b = 0;
    let n = 0;
    for (let i = 0; i < data.length; i += 4) {
      r += data[i];
      g += data[i + 1];
      b += data[i + 2];
      n += 1;
    }
    if (n === 0) return null;
    return { r: r / n, g: g / n, b: b / n };
  } catch {
    return null;
  }
}
